import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Typography, Card, CardContent, Grid, List, ListItem, ListItemText, ListItemIcon, Chip, Tooltip, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import ModernToggle from "../ModernToggle";
import GradientHeatmap from "../GradientHeatmap";
import { Info } from "@mui/icons-material";
import { BarChart as MuiBarChart } from "@mui/x-charts";
import { Divider } from "@mui/material";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

// Likert Analysis Component with Heatmap and Bar Chart Toggle
const LikertsSection = ({ likertData, likertSentiment }) => {
    const intl = useIntl();
    const [viewMode, setViewMode] = useState("barchart"); // "heatmap" or "barchart"
    const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
    const [questionDetailsModalOpen, setQuestionDetailsModalOpen] = useState(false);
    const [questionDetailsModalIndex, setQuestionDetailsModalIndex] = useState(null);
    const [questionDetailsLikertChoiceIndex, setQuestionDetailsLikertChoiceIndex] = useState(0);
    const [questionDetailsPctDiffExpanded, setQuestionDetailsPctDiffExpanded] = useState(false);
    const barChartContainerRef = React.useRef(null);
    const [barChartWidth, setBarChartWidth] = useState(400);

    useEffect(() => {
        if (viewMode !== "barchart") return;
        const el = barChartContainerRef.current;
        if (!el) return;
        const updateWidth = () => setBarChartWidth(Math.max(el.offsetWidth || 400, 280));
        updateWidth();
        const ro = new ResizeObserver(updateWidth);
        ro.observe(el);
        return () => ro.disconnect();
    }, [viewMode, selectedQuestionIndex]);

    if (!likertData) return null;

    // Sentiment for the selected question (likert_sentiment is ordered same as questions)
    const selectedSentiment = likertSentiment?.[selectedQuestionIndex]?.sentiment;

    const { questions, responses, response_types } = likertData;

    const getColorIntensity = (value, maxValue) => {
        const intensity = value / maxValue;
        return `rgba(25, 118, 210, ${intensity})`;
    };

    const getSentimentColor = (diff) => {
        if (diff > 0) return "#4caf50";
        if (diff < 0) return "#f44336";
        return "#ff9800";
    };

    // Resolve likert_insights key for a question text (keys may have leading/trailing spaces)
    const getLikertInsightsForQuestionText = (questionText, likertInsights) => {
        if (!likertInsights || questionText == null) return undefined;
        const trimmed = (questionText || '').trim();
        if (!trimmed) return undefined;
        if (likertInsights[questionText] !== undefined) return likertInsights[questionText];
        const keys = Object.keys(likertInsights);
        const exactTrimmed = keys.find((k) => (k || '').trim() === trimmed);
        if (exactTrimmed !== undefined) return likertInsights[exactTrimmed];
        const containsMatch = keys.find((k) => {
            const kTrim = (k || '').trim();
            return kTrim && (kTrim.includes(trimmed) || trimmed.includes(kTrim));
        });
        if (containsMatch !== undefined) return likertInsights[containsMatch];
        return undefined;
    };

    const maxValue = Math.max(...responses.flat());

    // Helper to check if a choice is empty/skipped
    const isEmptyChoice = (choice) =>
        choice === "" || choice === null || (typeof choice === 'string' && choice.trim() === '');

    // Find a matching response type without empty choices to get correct ordering
    const findMatchingCleanResponseType = (responseTypeWithEmpty) => {
        const allChoices = responseTypeWithEmpty.choices || [];
        const nonEmptyChoices = allChoices.filter(c => !isEmptyChoice(c));
        const nonEmptySet = new Set(nonEmptyChoices);

        // Look for another response type with exact same choices but no empty one
        for (const rt of response_types) {
            if (rt.id === responseTypeWithEmpty.id) continue;

            const rtChoices = rt.choices || [];
            // Check if this response type has no empty choices
            const hasEmpty = rtChoices.some(c => isEmptyChoice(c));
            if (hasEmpty) continue;

            // Check if it has the exact same choices (regardless of order)
            if (rtChoices.length !== nonEmptyChoices.length) continue;

            const rtSet = new Set(rtChoices);
            const isMatch = nonEmptyChoices.every(c => rtSet.has(c)) && rtChoices.every(c => nonEmptySet.has(c));

            if (isMatch) {
                return rt; // Found a matching clean response type
            }
        }
        return null;
    };

    // Get choices for current question type with empty filtering and correct ordering
    const getChoicesForQuestion = (questionIndex) => {
        const questionType = questions[questionIndex].type;
        const responseType = response_types.find(rt => rt.id === questionType);
        if (!responseType) return { validChoices: [], validIndices: [], skippedCount: 0, skippedIndex: -1, orderedChoices: [], orderedResponses: [] };

        const allChoices = responseType.choices || [];
        const questionResponses = responses[questionIndex] || [];

        // Find empty/skipped choice and track its count
        let skippedCount = 0;
        let skippedIndex = -1;
        const validChoices = [];
        const validIndices = [];

        allChoices.forEach((choice, index) => {
            if (isEmptyChoice(choice)) {
                skippedCount = questionResponses[index] || 0;
                skippedIndex = index;
            } else {
                validChoices.push(choice);
                validIndices.push(index);
            }
        });

        // If there was an empty choice, try to find correct ordering from a clean response type
        let orderedChoices = validChoices;
        let orderedResponses = validIndices.map(idx => questionResponses[idx] || 0);

        if (skippedIndex !== -1) {
            const cleanResponseType = findMatchingCleanResponseType(responseType);
            if (cleanResponseType) {
                // Use the ordering from the clean response type
                const cleanChoices = cleanResponseType.choices || [];
                orderedChoices = cleanChoices;

                // Reorder responses to match the clean ordering
                orderedResponses = cleanChoices.map(cleanChoice => {
                    const originalIndex = allChoices.findIndex(c => c === cleanChoice);
                    return originalIndex !== -1 ? (questionResponses[originalIndex] || 0) : 0;
                });
            }
        }

        return { validChoices, validIndices, skippedCount, skippedIndex, orderedChoices, orderedResponses };
    };

    // Get filtered responses (excluding skipped) with correct ordering
    const getFilteredResponses = (questionIndex) => {
        const { orderedResponses } = getChoicesForQuestion(questionIndex);
        return orderedResponses;
    };

    // Prepare bar chart data for selected question (filtered and ordered)
    const selectedQuestion = questions[selectedQuestionIndex];
    const { orderedChoices: selectedChoices, skippedCount: selectedSkippedCount, orderedResponses: selectedResponses } = getChoicesForQuestion(selectedQuestionIndex);

    const barChartData = selectedChoices.map((choice, index) => ({
        choice,
        count: selectedResponses[index] || 0
    }));

    // Bar colors by position: 3 = R,A,G; 5 = R,R,A,G,G; 10 = R,R,R,A,A,A,A,G,G,G
    const BAR_RED = "#c62828";
    const BAR_AMBER = "#EFD700";
    const BAR_GREEN = "#2e7d32";
    const getBarColorMap = (choices) => {
        const n = choices.length;
        const values = [...choices];
        let colors = [];
        if (n === 3) colors = [BAR_RED, BAR_AMBER, BAR_GREEN];
        else if (n === 5) colors = [BAR_RED, BAR_RED, BAR_AMBER, BAR_GREEN, BAR_GREEN];
        else if (n === 10) colors = [BAR_RED, BAR_RED, BAR_RED, BAR_AMBER, BAR_AMBER, BAR_AMBER, BAR_AMBER, BAR_GREEN, BAR_GREEN, BAR_GREEN];
        else colors = values.map(() => "#0ea5e9");
        return { type: "ordinal", values, colors };
    };
    const barColorMap = barChartData.length > 0 ? getBarColorMap(barChartData.map((d) => d.choice)) : null;

    // Prepare filtered data for heatmap with correct ordering
    const filteredResponseTypes = response_types.map(rt => {
        const allChoices = rt.choices || [];
        const hasEmpty = allChoices.some(c => isEmptyChoice(c));

        if (hasEmpty) {
            // Try to find a clean response type with correct ordering
            const cleanRT = findMatchingCleanResponseType(rt);
            if (cleanRT) {
                return { ...rt, choices: cleanRT.choices };
            }
        }

        // No empty or no matching clean type found - just filter out empty
        const filteredChoices = allChoices.filter(choice => !isEmptyChoice(choice));
        return { ...rt, choices: filteredChoices };
    });

    // Filter responses matrix to exclude empty choice columns with correct ordering
    const filteredResponses = questions.map((question, qIndex) => {
        const { orderedResponses } = getChoicesForQuestion(qIndex);
        return orderedResponses;
    });

    // Get total skipped count across all questions for a response type
    const getTotalSkippedForQuestion = (questionIndex) => {
        const { skippedCount } = getChoicesForQuestion(questionIndex);
        return skippedCount;
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h6"><FormattedMessage id="likertsSection.likert_analysis" defaultMessage="Likert Scale Analysis" /></Typography>
                    <ModernToggle
                        leftLabel={intl.formatMessage({ id: "likertsSection.heatmap", defaultMessage: "Heatmap" })}
                        rightLabel={intl.formatMessage({ id: "likertsSection.bar_chart", defaultMessage: "Bar Chart" })}
                        checked={viewMode === "barchart"}
                        onChange={(checked) => setViewMode(checked ? "barchart" : "heatmap")}
                    />
                </Box>

                {viewMode === "heatmap" ? (
                    <GradientHeatmap
                        questions={questions}
                        responseCounts={filteredResponses}
                        responseTypes={filteredResponseTypes}
                        questionTextWidth={30}
                    />
                ) : (
                    <Grid container spacing={3}>
                        {/* Question List - 50% width, height matches bar chart container (400) */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    height: 400,
                                    overflowY: 'auto',
                                    pr: 2,
                                    direction: 'rtl', // Reverse text direction to move scrollbar to left
                                    '& > *': {
                                        direction: 'ltr', // Reset direction for content
                                    },
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#888',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: '#555',
                                        }
                                    }
                                }}
                            >
                                <List>
                                    {questions.map((question, index) => {
                                        const skippedCount = getTotalSkippedForQuestion(index);
                                        return (
                                            <ListItem
                                                key={index}
                                                button
                                                selected={selectedQuestionIndex === index}
                                                onClick={() => setSelectedQuestionIndex(index)}
                                                sx={{
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: selectedQuestionIndex === index ? 'primary.main' : '#e0e0e0',
                                                    bgcolor: selectedQuestionIndex === index ? 'primary.50' : 'transparent',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        bgcolor: selectedQuestionIndex === index ? 'primary.100' : '#f5f5f5',
                                                        borderColor: 'primary.main',
                                                        transform: 'translateX(4px)'
                                                    }
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <Chip
                                                        label={index + 1}
                                                        size="small"
                                                        color={selectedQuestionIndex === index ? "primary" : "default"}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <>
                                                            {/* Question text (same typograpy as before) */}
                                                            <Typography
                                                               variant="body2"
                                                               sx={{
                                                                   flex: 1,
                                                                   fontWeight:selectedQuestionIndex === index ? "medium" : "normal"
                                                               }}
                                                            >
                                                               {question.text} 
                                                            </Typography>
                                                        </>
                                                    }
                                                    
                                                />
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1}}>
                                                {/* Pillar chip (NEW does not change layout structure */}
                                                {(question.pillar) && (
                                                                <Chip
                                                                    label={question.pillar}
                                                                    size="small"
                                                                    sx={{
                                                                        flexShrink: 0,
                                                                        fontSize: "0.8rem",
                                                                        height:20,
                                                                        bgcolor: "#e3f2fd",
                                                                        color: "#156c0",
                                                                        fontWeight: 600
                                                                    }}
                                                                />    
                                                            )}
                                                <Tooltip title={intl.formatMessage({ id: "likertsSection.view_details", defaultMessage: "View details" })} arrow placement="top">
                                                    <Box
                                                        component="span"
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setQuestionDetailsModalIndex(index);
                                                            setQuestionDetailsLikertChoiceIndex(0);
                                                            setQuestionDetailsModalOpen(true);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setQuestionDetailsModalIndex(index);
                                                                setQuestionDetailsLikertChoiceIndex(0);
                                                                setQuestionDetailsModalOpen(true);
                                                            }
                                                        }}
                                                        sx={{ display: 'inline-flex', cursor: 'pointer' }}
                                                    >
                                                        <Info
                                                            sx={{ fontSize: 18, color: selectedQuestionIndex === index ? 'primary.main' : 'text.secondary' }}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Box>
                        </Grid>

                        {/* Bar Chart - 50% width */}
                        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'relative'
                                }}
                            >
                                {/* Sentiment metric cards (red, amber, green) */}
                                {selectedSentiment && (() => {
                                    const total = selectedSentiment.total ?? ((selectedSentiment.negative_count ?? 0) + (selectedSentiment.neutral_count ?? 0) + (selectedSentiment.positive_count ?? 0));
                                    const tooltipTitle = (count) => (
                                        <Box component="span" sx={{ display: 'block', textAlign: 'center' }}>
                                            {intl.formatMessage({ id: "likertsSection.responses_tooltip", defaultMessage: "{count} / {total} responses" }, { count, total })}
                                        </Box>
                                    );
                                    return (
                                        <Grid container spacing={2} sx={{ mb: 2, width: '100%', maxWidth: 700 }}>
                                            <Grid item xs={4}>
                                                <Tooltip title={tooltipTitle(selectedSentiment.negative_count ?? 0)} arrow placement="top">
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 1.5,
                                                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                                                            border: '1px solid rgba(211, 47, 47, 0.3)',
                                                            textAlign: 'center',
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        <Typography variant="h6" sx={{ color: '#b71c1c', fontWeight: 700 }}>
                                                            {selectedSentiment.negative_pct ?? 0}%
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#c62828', opacity: 0.9 }}>
                                                            {selectedSentiment.negative_count ?? 0}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Tooltip title={tooltipTitle(selectedSentiment.neutral_count ?? 0)} arrow placement="top">
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 1.5,
                                                            bgcolor: 'rgba(255, 152, 0, 0.08)',
                                                            border: '1px solid rgba(255, 152, 0, 0.3)',
                                                            textAlign: 'center',
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        <Typography variant="h6" sx={{ color: '#ef6c00', fontWeight: 700 }}>
                                                            {selectedSentiment.neutral_pct ?? 0}%
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#e65100', opacity: 0.9 }}>
                                                            {selectedSentiment.neutral_count ?? 0}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Tooltip title={tooltipTitle(selectedSentiment.positive_count ?? 0)} arrow placement="top">
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 1.5,
                                                            bgcolor: 'rgba(46, 125, 50, 0.08)',
                                                            border: '1px solid rgba(46, 125, 50, 0.3)',
                                                            textAlign: 'center',
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        <Typography variant="h6" sx={{ color: '#1b5e20', fontWeight: 700 }}>
                                                            {selectedSentiment.positive_pct ?? 0}%
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#2e7d32', opacity: 0.9 }}>
                                                            {selectedSentiment.positive_count ?? 0}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    );
                                })()}

                                <Box
                                    ref={barChartContainerRef}
                                    sx={{ width: '100%', height: 400, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'stretch', minWidth: 0 }}
                                >
                                    {/* Skipped indicator */}

                                    {/* Bar Chart - stretches to container width */}
                                    {barChartData.length > 0 ? (
                                        <MuiBarChart
                                            dataset={barChartData}
                                            xAxis={[{
                                                scaleType: 'band',
                                                dataKey: 'choice',
                                                ...(barColorMap && { colorMap: barColorMap }),
                                                tickLabelStyle: {
                                                    fontSize: 12,
                                                    fill: '#666'
                                                }
                                            }]}
                                            yAxis={[{
                                                tickLabelStyle: {
                                                    fontSize: 12,
                                                    fill: '#666'
                                                }
                                            }]}
                                            series={[{
                                                dataKey: 'count',
                                                color: '#0ea5e9',
                                                highlightScope: {
                                                    highlighted: 'item',
                                                    faded: 'global'
                                                }
                                            }]}
                                            width={barChartWidth}
                                            height={380}
                                            slotProps={{
                                                legend: { hidden: true }
                                            }}
                                            margin={{ top: 20, bottom: 80, left: 60, right: 10 }}
                                            sx={{
                                                '& .MuiBarElement-root': {
                                                    filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.15))',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))',
                                                    }
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            <FormattedMessage id="likertsSection.no_valid_data" defaultMessage="No valid response data for this question" />
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                )}

                {/* Question details modal */}
                <Dialog
                    open={questionDetailsModalOpen}
                    onClose={() => setQuestionDetailsModalOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    {questionDetailsModalIndex != null && (() => {
                        const qIndex = questionDetailsModalIndex;
                        const q = questions[qIndex];
                        const likertInsightsForQuestion = getLikertInsightsForQuestionText(q?.text, likertData?.likert_insights);
                        const choicesArray = Array.isArray(likertInsightsForQuestion) ? likertInsightsForQuestion : [];
                        const choiceIndex = Math.min(Math.max(0, questionDetailsLikertChoiceIndex), Math.max(0, choicesArray.length - 1));
                        const detailsFromJson = choicesArray[choiceIndex] ?? null;
                        const attrTopicCount = detailsFromJson?.attr_topic_count ?? {};
                        const attrDiff = detailsFromJson?.attr_diff ?? {};
                        const totalCount = detailsFromJson?.count ?? 0;
                        const likertValue = detailsFromJson?.likert_value ?? '—';

                        return (
                            <>
                                <DialogTitle>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1}}>
                                    <Typography variant="body2"
                                                sx={{
                                                    flex:1,
                                                    pr:1
                                                }}
                                    >
                                        <strong>{intl.formatMessage({ id: "likertsSection.question", defaultMessage: "Question {index}:" }, { index: qIndex + 1 })}</strong> {q?.text}</Typography>
                                    {q?.pillar && (
                                        <Chip
                                            label={q.pillar}
                                            size="small"
                                            sx={{
                                                flexShrink: 0,
                                                fontSize: "0.8rem",
                                                height: 20,
                                                bgcolor: "#e3f2fd",
                                                color: "#1565c0",
                                                fontWeight: 600
                                            }}
                                        />    
                                    )}
                                    </Box>
                                    {choicesArray.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                            {choicesArray.map((choice, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={choiceIndex === idx
                                                        ? `${choice?.likert_value ?? intl.formatMessage({ id: "likertsSection.choice", defaultMessage: "Choice {index}" }, { index: idx + 1 })} (${totalCount})`
                                                        : (choice?.likert_value ?? intl.formatMessage({ id: "likertsSection.choice", defaultMessage: "Choice {index}" }, { index: idx + 1 }))
                                                    }
                                                    size="small"
                                                    onClick={() => setQuestionDetailsLikertChoiceIndex(idx)}
                                                    color={choiceIndex === idx ? 'primary' : 'default'}
                                                    variant={choiceIndex === idx ? 'filled' : 'outlined'}
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}><FormattedMessage id="likertsSection.no_likert_insights" defaultMessage="No likert insights for this question." /></Typography>
                                    )}
                                </DialogTitle>
                                <DialogContent dividers>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                                        <Divider />

                                        {/* Percentage Difference by Filter - foldable */}
                                        {Object.keys(attrTopicCount).length > 0 && (
                                            <Accordion
                                                expanded={questionDetailsPctDiffExpanded}
                                                onChange={() => setQuestionDetailsPctDiffExpanded(!questionDetailsPctDiffExpanded)}
                                                sx={{ mb: 0, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: 1 }}
                                            >
                                                <AccordionSummary expandIcon={<ExpandMore />}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        <FormattedMessage id="likertsSection.percentage_diff_by_filter" defaultMessage="Percentage Difference by Filter" />
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ pt: 0 }}>
                                                    {Object.entries(attrTopicCount).map(([groupQuestion, attributes], groupIndex) => {
                                                        const counts = typeof attributes === 'object' && attributes !== null ? Object.entries(attributes) : [];
                                                        return (
                                                            <Box key={groupQuestion} sx={{ mb: groupIndex < Object.keys(attrTopicCount).length - 1 ? 2 : 0 }}>
                                                                <Box sx={{
                                                                    px: 1.5,
                                                                    py: 0.75,
                                                                    bgcolor: '#e8f4fc',
                                                                    borderRadius: 1,
                                                                    mb: 1,
                                                                    borderLeft: '3px solid #1976d2'
                                                                }}>
                                                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1565c0' }} title={groupQuestion}>
                                                                        {groupQuestion.trim()}
                                                                    </Typography>
                                                                </Box>
                                                                {counts.map(([attrName, count], choiceIndex) => {
                                                                    const diff = attrDiff[groupQuestion]?.[attrName];
                                                                    const diffNum = typeof diff === 'number' ? diff : 0;
                                                                    return (
                                                                        <Box
                                                                            key={attrName}
                                                                            sx={{
                                                                                mb: 1.5,
                                                                                ml: 1,
                                                                                borderRadius: 1,
                                                                                p: 1,
                                                                                bgcolor: choiceIndex % 2 === 0 ? 'white' : '#fafafa',
                                                                                border: '1px solid #eee'
                                                                            }}
                                                                        >
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                <Typography variant="body2">{attrName}</Typography>
                                                                                <Chip
                                                                                    label={`${(diffNum * 100).toFixed(2)}%`}
                                                                                    size="small"
                                                                                    sx={{ bgcolor: getSentimentColor(diffNum), color: 'white' }}
                                                                                />
                                                                            </Box>
                                                                            <Box sx={{
                                                                                width: '100%',
                                                                                height: 8,
                                                                                bgcolor: '#e0e0e0',
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                position: 'relative'
                                                                            }}>
                                                                                <Box sx={{
                                                                                    position: 'absolute',
                                                                                    left: '50%',
                                                                                    width: `${Math.min(Math.abs(diffNum) * 50, 50)}%`,
                                                                                    height: '100%',
                                                                                    bgcolor: getSentimentColor(diffNum),
                                                                                    transform: diffNum < 0 ? 'translateX(-100%)' : 'none',
                                                                                    transition: 'width 0.3s ease'
                                                                                }} />
                                                                                <Box sx={{
                                                                                    position: 'absolute',
                                                                                    left: '50%',
                                                                                    width: 2,
                                                                                    height: '100%',
                                                                                    bgcolor: '#000',
                                                                                    transform: 'translateX(-50%)'
                                                                                }} />
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        );
                                                    })}
                                                </AccordionDetails>
                                            </Accordion>
                                        )}

                                        {/* Response Count by Filter - same style as tab 2 */}
                                        {Object.keys(attrTopicCount).length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                                                    <FormattedMessage id="likertsSection.response_count_by_filter" defaultMessage="Response Count by Filter" />
                                                </Typography>
                                                {Object.entries(attrTopicCount).map(([groupQuestion, attributes], groupIndex) => {
                                                    const counts = typeof attributes === 'object' && attributes !== null ? Object.entries(attributes) : [];
                                                    return (
                                                        <Box key={groupQuestion} sx={{ mb: 3 }}>
                                                            <Box sx={{
                                                                px: 1.5,
                                                                py: 0.75,
                                                                bgcolor: '#e8f4fc',
                                                                borderRadius: 1,
                                                                mb: 1.5,
                                                                borderLeft: '3px solid #1976d2'
                                                            }}>
                                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1565c0' }} title={groupQuestion}>
                                                                    {groupQuestion.trim()}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, ml: 1 }}>
                                                                {counts.map(([attrName, count], choiceIndex) => {
                                                                    const n = Number(count) || 0;
                                                                    return (
                                                                        <Paper
                                                                            key={attrName}
                                                                            sx={{
                                                                                p: 1.5,
                                                                                minWidth: 100,
                                                                                textAlign: 'center',
                                                                                border: '1px solid #e0e0e0',
                                                                                bgcolor: choiceIndex % 2 === 0 ? 'white' : '#fafafa',
                                                                                transition: 'border-color 0.2s ease'
                                                                            }}
                                                                        >
                                                                            <Typography variant="h6" color="primary.main">
                                                                                {n}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                                {attrName}
                                                                            </Typography>
                                                                        </Paper>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}

                                        {Object.keys(attrTopicCount).length === 0 && (
                                            <Typography variant="body2" color="text.secondary"><FormattedMessage id="likertsSection.no_attr_breakdown" defaultMessage="No attribute breakdown available." /></Typography>
                                        )}
                                    </Box>
                                </DialogContent>
                            </>
                        );
                    })()}
                    <DialogActions>
                        <Button onClick={() => setQuestionDetailsModalOpen(false)}><FormattedMessage id="likertsSection.close" defaultMessage="Close" /></Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};
export default LikertsSection;